import _ from 'lodash';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import deviceStore from '../../../stores/deviceStore';
import modalStore, { ModalOption } from '../../../stores/modalStore';
import { useMobxInstance } from '../../../clientUtils/mobxUtil';
import Carousel, { CarouselProvider, useCarouselContext } from '../Carousel';
import AlertModal from './AlertModal';
import ConfirmModal from './ConfirmModal';
import CustomModal from './CustomModal';
import GuideModal from './GuideModal';
import LoginModal from './LoginModal';
import MenuModal from './MenuModal';
import SelectModal from './SelectModal';
import StockModal from './StockModal';
import { ModalActionButton, ModalCloseXButton } from './ModalButton';
import ErrorModal from './ErrorModal';

/**
 * InternalModal - 순수 라우터 역할
 * - 타입별로 적절한 모달 컴포넌트를 렌더링
 * - 각 모달이 자체적으로 레이아웃과 이벤트 처리를 담당
 */
const InternalModal = observer(({ modalId }: { modalId: string }) => {
    const modalItem = modalStore.modalMap.get(modalId)?.modalItem;

    if (!modalItem) {
        return null;
    }

    // 타입별 분기 - 각 모달이 독립적으로 렌더링
    switch (modalItem.option.type) {
        case 'alert':
            return <AlertModal modalId={modalId} option={modalItem.option} />;
        case 'confirm':
            return <ConfirmModal modalId={modalId} option={modalItem.option} />;
        case 'login':
            return <LoginModal modalId={modalId} option={modalItem.option} />;
        case 'guide':
            return <GuideModal modalId={modalId} option={modalItem.option} />;
        case 'custom':
            return <CustomModal modalId={modalId} option={modalItem.option} />;
        case 'select':
            return <SelectModal modalId={modalId} option={modalItem.option} />;
        case 'menu':
            return <MenuModal modalId={modalId} option={modalItem.option} />;
        case 'stock':
            return <StockModal modalId={modalId} option={modalItem.option} />;
        case 'error':
            return <ErrorModal modalId={modalId} option={modalItem.option} />;
        default:
            return null;
    }
});

/**
 * 모달 컨테이너
 * - 루트에 한 번만 붙여둔다
 */
export const ModalContainer = observer(() => {
    return (
        <div className="invisible fixed h-0 w-0">
            {_.map(Array.from(modalStore.modalMap.entries()), ([modalId, obj]) => {
                const modalItem = obj.modalItem;

                // 열린 상태의 모달만 실제로 렌더링한다
                if (modalItem?.state === 'opened' || modalItem?.state === 'closing') {
                    return <InternalModal key={modalId} modalId={modalId} />;
                }
                return null;
            })}
        </div>
    );
});

/**
 * Modal 컴포넌트 라이브러리
 * - 각 모달에서 조합하여 사용
 */

const Dialog = observer(
    ({
        modalId,
        children,
        className,
    }: {
        modalId: string;
        children: React.ReactNode;
        className?: string;
    }) => {
        const dialogRef = useRef<HTMLDialogElement>(null);
        const modalItem = modalStore.modalMap.get(modalId)?.modalItem;
        const option = modalItem?.option;

        useEffect(() => {
            const dialog = dialogRef.current;
            if (!dialog) {
                return;
            }

            // 모달 엘리먼트 저장
            modalStore.setModalElement(modalId, dialog);

            // 모달 열기
            _.delay(() => {
                dialog.showModal();
            }, 50);

            const onClose = () => {
                modalStore.prepareClose(modalId);

                const returnValue = modalItem?.returnValue;
                if (modalItem?.onClose) {
                    modalItem.onClose(returnValue);
                }

                _.delay(() => {
                    modalStore.completeClose(modalId);
                }, 500);
            };

            const onCancel = (e: Event) => {
                if (modalItem?.option.preventClose) {
                    e.preventDefault();
                }
            };

            dialog.addEventListener('close', onClose);
            dialog.addEventListener('cancel', onCancel);

            return () => {
                dialog.removeEventListener('close', onClose);
                dialog.removeEventListener('cancel', onCancel);
            };
        }, [modalId]);

        return (
            <dialog
                ref={dialogRef}
                className={twMerge(
                    'modal select-none',
                    // 모바일에서는 하단에 모달 박스 표시
                    'modal-bottom',
                    // 데스크탑에서는 중앙에 모달 박스 표시
                    'sm:modal-middle',
                    className,
                )}
            >
                <div
                    className={twMerge(
                        'modal-box container overflow-visible px-0 pt-6 pb-5',
                        // 데스크탑에서는 최대 너비 450px
                        // 단 options.maxWidth 가 있으면 그 값을 사용
                        option?.maxWidth
                            ? 'sm:max-w-[min(calc(100dvw-40px),var(--modal-max-width))]'
                            : 'sm:max-w-[450px]',
                    )}
                    style={
                        option?.maxWidth
                            ? ({
                                  ['--modal-max-width' as const]: option.maxWidth,
                              } as React.CSSProperties)
                            : undefined
                    }
                >
                    {/* Title */}
                    {option?.title && <Title option={option} />}
                    {children}
                    {/* Close X Button */}
                    {option?.showCloseButton && <ModalCloseXButton />}
                </div>
                {/* Backdrop */}
                {option?.backdrop && (
                    <form method="dialog" className="modal-backdrop">
                        {!option?.preventClose && <button>닫기</button>}
                    </form>
                )}
            </dialog>
        );
    },
);

const Title = observer(({ option }: { option: ModalOption }) => {
    if (!option.title) {
        return null;
    }

    return (
        <h3
            className={
                option.titleAlign === 'center'
                    ? 'mx-5 mb-4 w-full shrink-0 text-center text-xl font-bold'
                    : 'mx-5 mb-4 shrink-0 text-lg font-bold'
            }
        >
            {option.title}
        </h3>
    );
});

const BodyScroll = observer(
    ({ children, className }: { children: React.ReactNode; className?: string }) => {
        return (
            <div
                className={twMerge('max-h-[calc(90vh-8rem)] overflow-auto px-5', className)}
                style={{
                    WebkitOverflowScrolling: 'touch',
                    overscrollBehaviorY: 'contain',
                }}
            >
                {children}
            </div>
        );
    },
);

/**
 * BodyCarousel용 ViewStore
 * - hover 상태 관리
 */
class BodyCarouselViewStore {
    isHovered = false;

    constructor() {
        makeAutoObservable(this);
    }

    setHovered(hovered: boolean) {
        this.isHovered = hovered;
    }
}

/**
 * ModalCarousel - 캐러셀 레이아웃을 가진 모달
 * - CarouselProvider를 자동으로 제공
 * - 스크롤 영역과 좌/우 버튼, Dots를 포함
 * - children으로 Carousel.Slide들을 받음
 */
const ModalCarousel = observer(
    ({ children, className }: { children: React.ReactNode; className?: string }) => {
        const viewStore = useMobxInstance(BodyCarouselViewStore);

        return (
            <CarouselProvider autoHeight={true}>
                <div
                    className={twMerge('relative', className)}
                    onMouseEnter={() => {
                        viewStore.setHovered(true);
                    }}
                    onMouseLeave={() => {
                        viewStore.setHovered(false);
                    }}
                >
                    {/* 스크롤 영역: 캐러셀 슬라이드 내용 */}
                    <BodyScroll className="px-0">
                        <Carousel>
                            {/* 슬라이더를 넣을 때 모달 캐러셀 컨테이너로 감싸줘야 함 */}
                            {children}
                        </Carousel>
                    </BodyScroll>

                    {/* 이전 버튼 (데스크탑 전용) */}
                    {deviceStore.hasHoverCapability && (
                        <Carousel.PrevCircleButton
                            isShow={viewStore.isHovered}
                            className="absolute top-1/2 -left-5.5 -translate-y-1/2"
                        />
                    )}

                    {/* 다음 버튼 (데스크탑 전용) */}
                    {deviceStore.hasHoverCapability && (
                        <Carousel.NextCircleButton
                            isShow={viewStore.isHovered}
                            className="absolute top-1/2 -right-5.5 -translate-y-1/2"
                        />
                    )}

                    {/* Dots */}
                    <div className="mt-4 px-5">
                        <Carousel.Dots />
                    </div>
                </div>
            </CarouselProvider>
        );
    },
);

/*
 * 사용 예

<Modal.Dialog>
    <Modal.ModalCarousel>
        <ModalContent title="Title"> // 모달 컨텍스트를 사용하려면 컴포넌트를 분리해야 함
    </Modal.ModalCarousel>
</Modal.Dialog>

// ModalContent
const ModalContent = () => {
    const { carouselStore } = useCarouselContext();

    // 슬라이드 active 여부에 따라 fetch 를 지연하는 등의 처리를 하면 됨
    const isSlideActive = carouselStore.currentSlideIndex === 0;

    return (
        <Modal.ModalCarouselContainer>
            <Carousel.Slide className="px-5"> // 슬라이드 컴포넌트는 반드시 ModalCarouselContainer 안에 있어야 함
                <div>Content A</div>
            </Carousel.Slide>
            <Carousel.Slide className="px-5">
                <div>Content B</div>
            </Carousel.Slide>
        </Modal.ModalCarouselContainer>
    );
};

 */
const ModalCarouselContainer = observer(
    ({ children, className }: { children: React.ReactNode; className?: string }) => {
        const { emblaApi } = useCarouselContext();

        return (
            <Carousel.Container className={twMerge('gap-5', className)}>
                {children}
            </Carousel.Container>
        );
    },
);

/**
 * Modal 컴포넌트 - Compound Component 패턴
 */
export const Modal = Object.assign(
    {},
    {
        Dialog,
        Title,
        BodyScroll,
        ModalCarousel,
        ModalCarouselContainer,
        ActionButton: ModalActionButton,
        CloseXButton: ModalCloseXButton,
    },
);
